import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Box,
  ButtonBase,
  Chip,
  CircularProgress,
  Link,
  Modal,
  Stack,
  Typography,
} from "@mui/material";
import ViewQuiltIcon from "@mui/icons-material/ViewQuilt";
import PhotoFilterOutlinedIcon from "@mui/icons-material/PhotoFilterOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useNavigate } from "react-router-dom";
import { firebaseHelper, usePermissions } from "ui";
import { AuthContext } from "../../auth/Auth";
import { createBoardInProject } from "./createBoardInProject";
import { PROJECT_EVENTS, trackProjectEvent } from "../../libs/projectAnalytics";
import {
  createNewFFESchedule,
  saveFFESchedule,
} from "../../v2/apis/ffeSchedulesApi";

interface StartWithDialogProps {
  project: { id: string; name?: string };
  onClose: () => void;
}

// Secondary option (DS2): small square card, icon over title.
const SmallOptionCard = ({
  icon,
  title,
  busy,
  onClick,
}: {
  icon: React.ReactNode;
  title: string;
  busy?: boolean;
  onClick: () => void;
}) => (
  <ButtonBase
    disabled={busy}
    onClick={onClick}
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      textAlign: "left",
      p: 2.5,
      border: "1px solid #e5e5e5",
      borderRadius: 3.5,
      "&:hover": { borderColor: "#d4d4d4", backgroundColor: "#fafafa" },
    }}
  >
    <Box sx={{ color: "#1a1a1a", display: "flex" }}>
      {busy ? <CircularProgress size={22} /> : icon}
    </Box>
    <Typography sx={{ mt: 1.5, fontSize: 16, fontWeight: 800, color: "#111" }}>
      {title}
    </Typography>
  </ButtonBase>
);

/**
 * "What are you starting with?" — shown right after creating a project
 * (#3544 Phase 3, DS2 layout). The Material schedule is the recommended
 * primary option (#3559 FF&E editor); Board and Concept are secondary.
 */
export const StartWithDialog = ({ project, onClose }: StartWithDialogProps) => {
  const navigate = useNavigate();
  const { currentUser } = useContext(AuthContext);
  const { permissions } = usePermissions(currentUser?.uid, firebaseHelper.db);
  const [creatingBoard, setCreatingBoard] = useState(false);
  const [boardError, setBoardError] = useState(false);
  const [creatingSchedule, setCreatingSchedule] = useState(false);
  const [scheduleError, setScheduleError] = useState(false);

  // Ref-guarded so React 18 StrictMode's dev double-mount fires this once.
  const shownTrackedRef = useRef(false);
  useEffect(() => {
    if (shownTrackedRef.current) return;
    shownTrackedRef.current = true;
    trackProjectEvent(PROJECT_EVENTS.startWithShown);
  }, []);

  // Exactly one choice is reported per dialog: whichever path exits first wins,
  // so a click that also unmounts the modal can't double-count.
  const choiceTrackedRef = useRef(false);
  const trackChoice = (
    choice: "schedule" | "board" | "concept" | "skip" | "dismiss",
  ) => {
    if (choiceTrackedRef.current) return;
    choiceTrackedRef.current = true;
    trackProjectEvent(PROJECT_EVENTS.startWithChosen, {
      choice,
    });
  };

  // Both the "Skip" link and the Modal's onClose (backdrop / Esc) land here, and
  // they mean different things — a deliberate skip is a decision, a dismiss is
  // an accident. The caller says which.
  const goToProject = (method: "skip" | "dismiss") => {
    trackChoice(method);
    onClose();
    navigate(`/home/projects/${project.id}`);
  };

  // FF&E schedule (#3559): create for THIS project (projectId is required by
  // the data model) and open the editor.
  const handleSchedule = async () => {
    if (!currentUser?.uid || creatingSchedule) return;
    setCreatingSchedule(true);
    setScheduleError(false);
    try {
      const draft = createNewFFESchedule(currentUser.uid, project.id);
      await saveFFESchedule(draft);
      trackChoice("schedule");
      onClose();
      navigate(`/home/schedules/${draft.id}`);
    } catch {
      setCreatingSchedule(false);
      setScheduleError(true);
    }
  };

  const handleBoard = async () => {
    if (!currentUser?.uid) return;
    setCreatingBoard(true);
    setBoardError(false);
    try {
      const board = await createBoardInProject(
        project.id,
        currentUser.uid,
        permissions,
      );
      trackChoice("board");
      // Full reload to leave the SPA's /home tree (matches HomeSideBar's
      // Create New Board behavior).
      window.location.assign(`/board/${board.id}`);
    } catch {
      setCreatingBoard(false);
      setBoardError(true);
    }
  };

  return (
    <Modal
      open
      // A creation already in flight is a choice the user made; letting Esc or
      // the backdrop close over it would latch `dismiss` and permanently
      // suppress the real choice when the write resolves. Ignore the close —
      // it would also orphan the pending navigation.
      onClose={() => {
        if (creatingSchedule || creatingBoard) return;
        goToProject("dismiss");
      }}
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <Box
        sx={{
          width: 560,
          maxWidth: "92vw",
          bgcolor: "background.paper",
          borderRadius: 4,
          boxShadow: 24,
          p: { xs: 3, sm: 4 },
        }}
      >
        <Typography sx={{ fontWeight: 800, fontSize: 24, color: "#111" }}>
          What are you starting with?
        </Typography>
        <Typography sx={{ mt: 0.5, fontSize: 15, color: "#9a9aa0" }}>
          You can add the rest to {project.name || "this project"} anytime.
        </Typography>

        {/* Recommended: Material schedule (DS2) */}
        <ButtonBase
          disabled={creatingSchedule}
          onClick={handleSchedule}
          sx={{
            mt: 3,
            width: "100%",
            display: "flex",
            alignItems: "center",
            gap: 2,
            textAlign: "left",
            p: 2.5,
            border: "2px solid #4a8bf0",
            borderRadius: 4,
            backgroundColor: "#fbfcff",
            "&:hover": { borderColor: "#2f6fe0", backgroundColor: "#f5f8ff" },
          }}
        >
          <Box sx={{ color: "#2f6fe0", display: "flex", flexShrink: 0 }}>
            {creatingSchedule ? (
              <CircularProgress size={26} />
            ) : (
              <TableChartOutlinedIcon sx={{ fontSize: 30 }} />
            )}
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#111" }}>
                Material schedule
              </Typography>
              <Chip
                size="small"
                label="Recommended"
                sx={{
                  backgroundColor: "#e2edfd",
                  color: "#2f6fe0",
                  fontWeight: 700,
                  fontSize: 12,
                  height: 22,
                  borderRadius: 2,
                }}
              />
            </Stack>
            <Typography sx={{ mt: 0.5, fontSize: 14.5, color: "#6d6864" }}>
              Spec every material, finish, and fixture — boards and concepts
              build from it.
            </Typography>
          </Box>
          <ArrowForwardIcon sx={{ color: "#8c8c8e", flexShrink: 0 }} />
        </ButtonBase>

        {/* Secondary options */}
        <Box
          sx={{
            mt: 2,
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 1.75,
          }}
        >
          <SmallOptionCard
            icon={<ViewQuiltIcon sx={{ fontSize: 24 }} />}
            title="Board"
            busy={creatingBoard}
            onClick={handleBoard}
          />
          <SmallOptionCard
            icon={<PhotoFilterOutlinedIcon sx={{ fontSize: 24 }} />}
            title="Concept"
            onClick={() => {
              trackChoice("concept");
              onClose();
              // Carry the just-created project so the new concept is
              // associated with it by default (issue #3669).
              navigate(`/home/concepts/new?project=${project.id}`);
            }}
          />
        </Box>

        {boardError && (
          <Typography sx={{ mt: 1.5, fontSize: 13, color: "error.main" }}>
            Couldn't create the board — please try again.
          </Typography>
        )}
        {scheduleError && (
          <Typography sx={{ mt: 1.5, fontSize: 13, color: "error.main" }}>
            Couldn't create the schedule — please try again.
          </Typography>
        )}

        <Box sx={{ mt: 3, textAlign: "center" }}>
          <Link
            component="button"
            type="button"
            underline="hover"
            onClick={() => goToProject("skip")}
            sx={{ fontSize: 14, color: "text.secondary" }}
          >
            Skip — take me to the project
          </Link>
        </Box>
      </Box>
    </Modal>
  );
};
